import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Tab, Tabs } from 'react-bootstrap';
import { DocumentsStyles } from '../styles/DocumentsStyled';
import { toast } from 'react-toastify';

// Tabs
import ToBeCalledTab from './ToBeCalledTab';
import CalledTab from './CalledTab';
import PresetsTab from './PresetsTab';
import ActiveCallModal from './components/ActiveCallModal';

// helpers
import { getTeamIdFromURL } from '../../../../../_helpers';
import {
    fetchContactsAction,
    fetchMembersAction,
    fetchPresetsAction,
    fetchCallsAction,
    createCallAction,
    initiateCallAction,
    endCallAction,
    bargeIntoCallAction,
    listenToCallAction,
    createContactAction,
    bulkCreateContactsAction,
    setCallCenterError,
    clearCallCenterError,
    setActiveCall,
    clearActiveCall,
    fetchLabelsAction,
    fetchCampaignsAction,
    resetCallCenterState,
} from '../../../../../_redux/actions/ActionCallCenter';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import ActiveCallsTab from './ActiveCallsTab';
import CampaignsTab from './CampaignsTab';
import VerticalsTab from './VerticalsTab';

function Component({
    currUser,
    callCenter,
    resetCallCenterState,
    fetchContactsAction,
    fetchMembersAction,
    fetchCampaignsAction,
    fetchPresetsAction,
    fetchLabelsAction,
    fetchCallsAction,
    createCallAction,
    initiateCallAction,
    endCallAction,
    bargeIntoCallAction,
    listenToCallAction,
    createContactAction,
    bulkCreateContactsAction,
    setCallCenterError,
    clearCallCenterError,
    setActiveCall,
    clearActiveCall,
}) {
    const teamId = getTeamIdFromURL();
    const [key, setKey] = useState('to-be-called');
    const [showActiveCallModal, setShowActiveCallModal] = useState(false);
    const [actionLoading, setActionLoading] = useState({
        hangup: false,
        listen: false,
        barge: false,
    });

    const { activeCall, calls, loading, error } = callCenter || {};

    // Fetch call center data when component mounts
    useEffect(() => {
        if (teamId) {
            resetCallCenterState();
            fetchCallCenterData();
        }
    }, [teamId]);

    const fetchCallCenterData = async () => {
        try {
            await Promise.all([
                fetchContactsAction(teamId, { limit: 500, status: 'active' }),
                // fetchCampaignsAction(teamId),
                fetchPresetsAction(teamId),
                // fetchLabelsAction(teamId),
                // fetchCallsAction(teamId),
                // fetchMembersAction(teamId),
            ]);
        } catch (error) {
            console.error('Error fetching call center data:', error);
            toast.error('Failed to load call center data');
        }
    };

    const handleCreateCall = async (callData) => {
        try {
            if (!teamId) {
                throw new Error('Team not loaded');
            }

            const response = await createCallAction({
                ...callData,
                team: teamId,
                dialedByUser: currUser.id,
            });

            return response;
        } catch (error) {
            console.error('Error creating call:', error);
            throw error;
        }
    };

    const handleInitiateCall = async (callId) => {
        try {
            const response = await initiateCallAction(callId);

            // Open active call modal
            setShowActiveCallModal(true);
            return response;
        } catch (error) {
            console.error('Error initiating call:', error);
            throw error;
        }
    };

    // Active call actions
    const handleHangupActive = async (_data) => {
        const data = _data || activeCall;
        if (!data) return;
        setActionLoading((prev) => ({ ...prev, hangup: true }));
        try {
            const callId = data.id || data._id;
            await endCallAction(callId);
            setShowActiveCallModal(false);
            clearActiveCall();
        } catch (error) {
            console.error('Error hanging up call:', error);
        } finally {
            setActionLoading((prev) => ({ ...prev, hangup: false }));
        }
    };

    const handleListenInActive = async (_data) => {
        const data = _data || activeCall;
        if (!data) return;
        setActionLoading((prev) => ({ ...prev, listen: true }));
        try {
            const callId = data.id || data._id;
            await listenToCallAction(callId);
        } catch (error) {
            console.error('Error listening to call:', error);
        } finally {
            setActionLoading((prev) => ({ ...prev, listen: false }));
        }
    };

    const handleBargeInActive = async (_data) => {
        const data = _data || activeCall;
        if (!data) return;
        setActionLoading((prev) => ({ ...prev, barge: true }));
        try {
            const callId = data.id || data._id;
            await bargeIntoCallAction(callId);
        } catch (error) {
            console.error('Error barging into call:', error);
        } finally {
            setActionLoading((prev) => ({ ...prev, barge: false }));
        }
    };

    // Contact management
    const handleCreateContact = async (contactData) => {
        try {
            const response = await createContactAction({
                ...contactData,
                team: teamId,
            });
            toast.success('Contact created successfully');
            return response;
        } catch (error) {
            console.error('Error creating contact:', error);
            toast.error('Failed to create contact');
            throw error;
        }
    };

    const handleBulkCreateContacts = async (contactsData) => {
        try {
            const response = await bulkCreateContactsAction(contactsData, teamId);
            toast.success('Contacts imported successfully');
            return response;
        } catch (error) {
            console.error('Error bulk creating contacts:', error);
            toast.error('Failed to import contacts');
            throw error;
        }
    };

    // Close active call modal
    const handleCloseActiveCallModal = () => {
        setShowActiveCallModal(false);
        if (activeCall?.callStatus === 'completed') {
            clearActiveCall();
        }
    };

    return (
        <div css={DocumentsStyles}>
            <h1 className="mb-4">Call Center</h1>

            {/* {error && (
                <Alert variant="danger" className="mb-3">
                    <strong>Error:</strong> {error}
                    <Button variant="outline-danger" size="sm" className="ms-2" onClick={clearCallCenterError}>
                        Dismiss
                    </Button>
                </Alert>
            )} */}

            <Tabs id="call-center-tabs" activeKey={key} onSelect={(k) => setKey(k)} className="mb-3">
                <Tab eventKey="to-be-called" title="To Be Called">
                    {key === 'to-be-called' && (
                        <ToBeCalledTab
                            loading={loading}
                            onCreateContact={handleCreateContact}
                            onCreateCall={handleCreateCall}
                            onInitiateCall={handleInitiateCall}
                            onBulkCreateContacts={handleBulkCreateContacts}
                        />
                    )}
                </Tab>
                <Tab eventKey="called" title="Called">
                    {key === 'called' && (
                        <CalledTab
                            calls={calls}
                            loading={loading}
                            onBargeIn={handleBargeInActive}
                            onListenIn={handleListenInActive}
                            onShowCallDetails={(call) => {
                                setActiveCall(call);
                                setShowActiveCallModal(true);
                            }}
                        />
                    )}
                </Tab>
                <Tab eventKey="presets" title="Presets">
                    {key === 'presets' && <PresetsTab />}
                </Tab>
                <Tab eventKey="campaigns" title="Campaigns">
                    {key === 'campaigns' && <CampaignsTab />}
                </Tab>
                <Tab eventKey="verticals" title="Verticals">
                    {key === 'verticals' && <VerticalsTab />}
                </Tab>
                <Tab eventKey="active-calls" title="Active Calls">
                    {key === 'active-calls' && (
                        <ActiveCallsTab
                            onHangup={handleHangupActive}
                            onListenIn={handleListenInActive}
                            onBargeIn={handleBargeInActive}
                            hangupLoading={actionLoading.hangup}
                            listenLoading={actionLoading.listen}
                            bargeLoading={actionLoading.barge}
                        />
                    )}
                </Tab>
            </Tabs>

            {/* Active Call Modal */}
            {showActiveCallModal && (
                <ActiveCallModal
                    show={showActiveCallModal}
                    onClose={handleCloseActiveCallModal}
                    activeCallId={(activeCall || {})?._id}
                    onHangup={handleHangupActive}
                    onListenIn={handleListenInActive}
                    onBargeIn={handleBargeInActive}
                    hangupLoading={actionLoading.hangup}
                    listenLoading={actionLoading.listen}
                    bargeLoading={actionLoading.barge}
                />
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({
    callCenter: state.callCenter,
    currUser: state.user.user,
});

const mapDispatchToProps = {
    fetchContactsAction,
    fetchMembersAction,
    fetchPresetsAction,
    fetchCampaignsAction,
    fetchLabelsAction,
    fetchCallsAction,
    createCallAction,
    initiateCallAction,
    endCallAction,
    bargeIntoCallAction,
    listenToCallAction,
    createContactAction,
    bulkCreateContactsAction,
    setCallCenterError,
    clearCallCenterError,
    setActiveCall,
    clearActiveCall,
    resetCallCenterState,
};

const CallCenterComponent = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, mapDispatchToProps)(CallCenterComponent);
